<template>
  <div class="funcion-page">
    <div class="container">
      <div class="funcion-content">

        <!-- Not found -->
        <div v-if="notFound" style="color: #fff; padding: 40px 0;">
          <h1>Evento no encontrado</h1>
          <router-link to="/cartelera" style="color: #c9a227;">← Volver a la cartelera</router-link>
        </div>

        <template v-else-if="evento">
          <!-- Event Title -->
          <h1 class="funcion-title">{{ evento.titulo }} <span v-if="evento.origen" class="funcion-origin">({{ evento.origen }})</span></h1>

          <!-- Date & Time -->
          <div class="funcion-date">
            <svg class="calendar-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span><strong>Fecha:</strong> {{ evento.fecha }}</span>
          </div>

          <!-- Venue / Location -->
          <div class="funcion-venue" v-if="evento.venue">
            <svg class="venue-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <span>
              <strong>Lugar: </strong>
              <a :href="evento.venue.mapUrl" target="_blank" rel="noopener" class="venue-link">
                {{ evento.venue.name }} 📍
              </a>
            </span>
          </div>

          <!-- STANDARD LAYOUT (single show) -->
          <template v-if="!evento.subShows">
            <!-- Description -->
            <div class="funcion-description">
              <p v-for="(parrafo, index) in evento.descripcion" :key="index">{{ parrafo }}</p>
            </div>

            <!-- Age Rating -->
            <div class="funcion-age">
              <strong>Edad mínima:</strong> {{ evento.edad }}
            </div>

            <!-- Event Image -->
            <div class="funcion-image" v-if="evento.imagen">
              <img :src="evento.imagen" :alt="evento.titulo">
            </div>

            <!-- Tickets Section -->
            <div class="tickets-section">
              <h2 class="tickets-title">Entradas</h2>

              <!-- In-person tickets -->
              <div class="ticket-option">
                <div class="ticket-icon">🎭</div>
                <div class="ticket-info">
                  <h3>En boletería</h3>
                  <p>Presentarse <strong>30 minutos antes</strong> de la función</p>
                  <div class="ticket-price">${{ evento.precio }}</div>
                </div>
              </div>

              <!-- MercadoPago online tickets -->
              <div class="ticket-option ticket-online" v-if="evento.id && evento.isUpcoming">
                <div class="ticket-icon">🖥️</div>
                <div class="ticket-info">
                  <h3>Comprá online</h3>
                  <p>Pagá con tarjeta de débito o crédito de forma segura</p>

                  <!-- Quantity selector -->
                  <div class="quantity-selector">
                    <button class="qty-btn" @click="decreaseQty" :disabled="quantity <= 1">−</button>
                    <span class="qty-value">{{ quantity }}</span>
                    <button class="qty-btn" @click="increaseQty" :disabled="quantity >= 10">+</button>
                    <span class="qty-total">${{ evento.precio_mp * quantity }} UYU</span>
                  </div>

                  <!-- MP Button -->
                  <button
                    class="mp-button"
                    @click="comprarOnline"
                    :disabled="mpLoading"
                  >
                    <span v-if="mpLoading" class="mp-loading">Redirigiendo...</span>
                    <span v-else class="mp-button-content">
                      <img src="/images/mp-logo.png" alt="" class="mp-logo" onerror="this.style.display='none'">
                      Comprar con Mercado Pago
                    </span>
                  </button>

                  <p class="mp-note">Serás redirigido a MercadoPago para completar el pago</p>
                </div>
              </div>

              <!-- RedTickets online tickets -->
              <div class="ticket-option ticket-online" v-if="evento.redtickets">
                <div class="ticket-icon">🎟️</div>
                <div class="ticket-info">
                  <h3>Compra anticipada</h3>
                  <p>Comprá tu entrada online a través de RedTickets</p>
                  <a :href="evento.redtickets" target="_blank" rel="noopener" class="redtickets-link">
                    <img src="/images/RedTicket.jpg" alt="Comprar en RedTickets" class="redtickets-img">
                  </a>
                </div>
              </div>
            </div>
          </template>

          <!-- SUB-SHOWS LAYOUT (multiple shows in one event) -->
          <template v-else>
            <!-- Age Rating (shared) -->
            <div class="funcion-age">
              <strong>Edad mínima:</strong> {{ evento.edad }}
            </div>

            <!-- Each sub-show -->
            <div v-for="(sub, index) in evento.subShows" :key="index" class="sub-show-block">
              <div class="sub-show-header">
                <span class="sub-show-time">{{ sub.time }}</span>
                <h2 class="sub-show-title">{{ sub.titulo }} <span class="funcion-origin">({{ sub.origen }})</span></h2>
              </div>

              <div class="funcion-description">
                <p v-for="(parrafo, pIndex) in sub.descripcion" :key="pIndex">{{ parrafo }}</p>
              </div>

              <div class="funcion-image" v-if="sub.imagen">
                <img :src="sub.imagen" :alt="sub.titulo">
              </div>

              <!-- Sub-show tickets -->
              <div class="tickets-section sub-show-tickets">
                <h3 class="tickets-title">Entradas — {{ sub.titulo }}</h3>

                <div class="ticket-option">
                  <div class="ticket-icon">🎭</div>
                  <div class="ticket-info">
                    <h3>En boletería</h3>
                    <p>Presentarse <strong>30 minutos antes</strong> de la función</p>
                    <div class="ticket-price">${{ evento.precio }}</div>
                  </div>
                </div>

                <div class="ticket-option ticket-online" v-if="sub.redtickets">
                  <div class="ticket-icon">🎟️</div>
                  <div class="ticket-info">
                    <h3>Compra anticipada</h3>
                    <p>Comprá tu entrada online a través de RedTickets</p>
                    <a :href="sub.redtickets" target="_blank" rel="noopener" class="redtickets-link">
                      <img src="/images/RedTicket.jpg" alt="Comprar en RedTickets" class="redtickets-img">
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FuncionPage',
  data() {
    return {
      evento: null,
      notFound: false,
      quantity: 1,
      mpLoading: false
    }
  },
  async mounted() {
    await this.fetchFuncion()
  },
  watch: {
    '$route.params.slug'() {
      this.fetchFuncion()
    }
  },
  methods: {
    async fetchFuncion() {
      const slug = this.$route.params.slug
      this.evento = null
      this.notFound = false
      this.quantity = 1
      try {
        const res = await fetch(`/api/funciones/${slug}`)
        if (!res.ok) {
          this.notFound = true
          return
        }
        const f = await res.json()

        // Determine if show is upcoming
        const showDate = new Date(f.date)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const isUpcoming = showDate >= today

        this.evento = {
          id: f.id,
          titulo: f.titulo,
          origen: f.origen,
          fecha: f.fecha_display,
          venue: f.venue,
          descripcion: f.descripcion || [],
          subShows: f.sub_shows || null,
          edad: f.edad,
          precio: f.precio,
          precio_mp: f.precio_mp || f.precio,
          imagen: f.imagen,
          redtickets: f.redtickets || null,
          isUpcoming
        }
      } catch (e) {
        console.error('Error loading funcion:', e)
        this.notFound = true
      }
    },

    increaseQty() {
      if (this.quantity < 10) this.quantity++
    },

    decreaseQty() {
      if (this.quantity > 1) this.quantity--
    },

    async comprarOnline() {
      if (this.mpLoading) return
      this.mpLoading = true
      try {
        const res = await fetch('/api/payments/create-preference', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            funcion_id: this.evento.id,
            quantity: this.quantity
          })
        })
        const data = await res.json()
        if (!res.ok) {
          alert('Error al crear el pago. Por favor intentá de nuevo.')
          return
        }
        // Redirect to MercadoPago checkout
        window.location.href = data.init_point
      } catch (e) {
        console.error('MP error:', e)
        alert('Error al conectar con MercadoPago. Por favor intentá de nuevo.')
      } finally {
        this.mpLoading = false
      }
    }
  }
}
</script>

<style scoped>
.funcion-page {
  background: #000;
  padding: 60px 0 80px;
  min-height: 50vh;
}

.funcion-content {
  max-width: 800px;
  margin: 0 auto;
  color: white;
}

/* Festival Badge */
.festival-badge {
  display: inline-block;
  background: linear-gradient(135deg, #c9a227 0%, #d4af37 50%, #c9a227 100%);
  color: #000;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: 4px;
  margin-bottom: 20px;
}

/* Event Title */
.funcion-title {
  font-size: 42px;
  font-weight: bold;
  color: #fff;
  margin: 0 0 25px 0;
  line-height: 1.2;
}

.funcion-origin {
  font-weight: 400;
  color: #ccc;
  font-size: 32px;
}

/* Date */
.funcion-date {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  color: #fff;
  margin-bottom: 10px;
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-left: 4px solid #c9a227;
  border-radius: 0 8px 8px 0;
}

.calendar-icon {
  color: #c9a227;
  flex-shrink: 0;
}

/* Venue */
.funcion-venue {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  color: #fff;
  margin-bottom: 35px;
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-left: 4px solid #c9a227;
  border-radius: 0 8px 8px 0;
}

.venue-icon {
  color: #c9a227;
  flex-shrink: 0;
}

.venue-link {
  color: #c9a227;
  text-decoration: none;
  transition: color 0.3s ease;
}

.venue-link:hover {
  color: #e0c040;
  text-decoration: underline;
}

/* Description */
.funcion-description {
  margin-bottom: 30px;
}

.funcion-description p {
  font-size: 18px;
  line-height: 1.8;
  color: #ddd;
  margin: 0 0 20px 0;
  text-align: justify;
}

.funcion-description p:last-child {
  margin-bottom: 0;
}

/* Age Rating */
.funcion-age {
  font-size: 16px;
  color: #aaa;
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  margin-bottom: 40px;
}

/* Event Image */
.funcion-image {
  margin-top: 40px;
  text-align: center;
}

.funcion-image img {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

/* Tickets Section */
.tickets-section {
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #333;
}

/* Sub-show blocks */
.sub-show-block {
  margin-top: 50px;
  padding-top: 40px;
  border-top: 1px solid #333;
}

.sub-show-block:first-of-type {
  margin-top: 30px;
  border-top: none;
  padding-top: 0;
}

.sub-show-header {
  margin-bottom: 25px;
}

.sub-show-time {
  display: inline-block;
  background: #c9a227;
  color: #000;
  font-size: 14px;
  font-weight: bold;
  padding: 5px 14px;
  border-radius: 4px;
  margin-bottom: 12px;
  letter-spacing: 0.05em;
}

.sub-show-title {
  font-size: 32px;
  font-weight: bold;
  color: #fff;
  margin: 0;
  line-height: 1.2;
}

.sub-show-tickets {
  margin-top: 30px;
  padding-top: 25px;
}

.sub-show-tickets .tickets-title {
  font-size: 22px;
}

.tickets-title {
  font-size: 32px;
  font-weight: bold;
  color: #fff;
  margin: 0 0 30px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ticket-option {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 25px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  margin-bottom: 20px;
  border: 1px solid #222;
  transition: all 0.3s ease;
}

.ticket-option:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: #333;
}

.ticket-icon {
  font-size: 36px;
  flex-shrink: 0;
}

.ticket-info {
  flex: 1;
}

.ticket-info h3 {
  font-size: 20px;
  font-weight: bold;
  color: #fff;
  margin: 0 0 10px 0;
}

.ticket-info p {
  font-size: 16px;
  color: #aaa;
  margin: 0 0 15px 0;
  line-height: 1.5;
}

.ticket-price {
  display: inline-block;
  font-size: 28px;
  font-weight: bold;
  color: #c9a227;
}

/* RedTickets Link */
.redtickets-link {
  display: inline-block;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.redtickets-link:hover {
  transform: scale(1.05);
}

.redtickets-img {
  max-width: 250px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.redtickets-link:hover .redtickets-img {
  box-shadow: 0 8px 25px rgba(201, 162, 39, 0.3);
}

/* Quantity Selector */
.quantity-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.qty-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #c9a227;
  background: transparent;
  color: #c9a227;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  line-height: 1;
}

.qty-btn:hover:not(:disabled) {
  background: #c9a227;
  color: #000;
}

.qty-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.qty-value {
  font-size: 22px;
  font-weight: bold;
  color: #fff;
  min-width: 30px;
  text-align: center;
}

.qty-total {
  font-size: 22px;
  font-weight: bold;
  color: #c9a227;
  margin-left: 8px;
}

/* MercadoPago Button */
.mp-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 380px;
  padding: 16px 24px;
  background: #009ee3;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 0.02em;
}

.mp-button:hover:not(:disabled) {
  background: #007bbf;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 158, 227, 0.4);
}

.mp-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.mp-button-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mp-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.mp-loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mp-loading::before {
  content: '';
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.mp-note {
  font-size: 13px !important;
  color: #666 !important;
  margin-top: 10px !important;
  margin-bottom: 0 !important;
}

/* Mobile */
@media (max-width: 767px) {
  .funcion-page {
    padding: 40px 20px 60px;
  }

  .funcion-title {
    font-size: 28px;
  }

  .funcion-origin {
    font-size: 22px;
    display: block;
    margin-top: 5px;
  }

  .funcion-date {
    font-size: 16px;
    padding: 12px 15px;
  }

  .funcion-venue {
    font-size: 16px;
    padding: 12px 15px;
  }

  .funcion-description p {
    font-size: 16px;
    text-align: left;
  }

  .festival-badge {
    font-size: 12px;
    padding: 6px 15px;
  }

  /* Mobile Tickets */
  .tickets-section {
    margin-top: 40px;
    padding-top: 30px;
  }

  .tickets-title {
    font-size: 24px;
    margin-bottom: 20px;
  }

  .ticket-option {
    flex-direction: column;
    gap: 15px;
    padding: 20px;
  }

  .ticket-icon {
    font-size: 30px;
  }

  .ticket-info h3 {
    font-size: 18px;
  }

  .ticket-info p {
    font-size: 15px;
  }

  .ticket-price {
    font-size: 24px;
  }

  .redtickets-img {
    max-width: 200px;
  }

  .sub-show-title {
    font-size: 24px;
  }

  .sub-show-tickets .tickets-title {
    font-size: 18px;
  }

  .mp-button {
    max-width: 100%;
    font-size: 15px;
    padding: 14px 20px;
  }

  .qty-total {
    font-size: 18px;
  }
}
</style>